import { supabase } from './supabase.js';
import { resolveProfileAvatarUrl } from './profileAvatar.js';
import { PAISES_COMUN } from '../marketplace/data/spanishProvinces.js';
import { isHttpImagenUrl } from './comunidadImagenesService.js';
import {
  COMUNIDAD_GENERO_DESCONOCIDO,
  COMUNIDAD_GENERO_MULTICULTURAL,
  musicGenresFromCuerpo,
  musicGenreModeFromGenres,
  normalizeComunidadMusicGenresForSave,
} from './comunidadMusicGenres.js';

const TIPO_EVENTO = 'Evento en vivo';

/** Opciones del desplegable «Qué publicas» (value = BD; label = UI). */
export const COMUNIDAD_TIPOS_PUBLICACION = [
  { value: 'Evento en vivo', label: 'Evento en vivo (conciertos, festivales, etc.)' },
  { value: 'Tienda de vinilos', label: 'Tienda de vinilos' },
  { value: 'Pub / bar', label: 'Pub / bar' },
  { value: 'Discoteca / sala', label: 'Discoteca / sala' },
  { value: 'Mercadillo u ocasión', label: 'Mercadillo u ocasión' },
  { value: 'Otro (lugar musical)', label: 'Otro (lugar musical)' },
];

export function comunidadTipoPublicacionLabel(value) {
  const v = String(value ?? '').trim();
  const row = COMUNIDAD_TIPOS_PUBLICACION.find(t => t.value === v);
  return row?.label ?? (v || '—');
}

const TIPO_CORTO_HEADER = {
  'Tienda de vinilos': 'Tienda',
  'Pub / bar': 'Pub',
  'Discoteca / sala': 'Sala',
  'Mercadillo u ocasión': 'Mercadillo',
  'Otro (lugar musical)': 'Lugar',
};

function paisLabel(v) {
  return PAISES_COMUN.find(p => p.v === v)?.l || v;
}

/** Solo URLs http(s) en BD; data:/blob: deben subirse antes vía Storage. */
export function normalizeComunidadImagenUrl(value) {
  const s = String(value ?? '').trim();
  if (!s) return null;
  if (isHttpImagenUrl(s)) return s;
  return null;
}

function buildLocationPlaceFromForm(f) {
  const parts = [];
  if (String(f.via || '').trim()) parts.push(String(f.via).trim());
  if (String(f.ciudad || '').trim()) parts.push(String(f.ciudad).trim());
  if (String(f.cp || '').trim()) parts.push(String(f.cp).trim());
  if (f.pais === 'ES' && f.provincia) parts.push(f.provincia);
  else if (f.pais && f.pais !== 'ES' && String(f.provinciaTexto || '').trim()) parts.push(String(f.provinciaTexto).trim());
  if (f.pais) parts.push(paisLabel(f.pais));
  return parts.length ? parts.join(' · ') : '';
}

/**
 * Columnas editables de `comunidad_publicaciones` desde el formulario (sin autor ni flags).
 * @param {object} form — mismo shape que `defaultForm` + imagenPreview
 */
export function publicationBodyFromForm(form) {
  const isEvento = form.tipo === TIPO_EVENTO;
  const place = buildLocationPlaceFromForm(form);
  let entrada_tipo = null;
  let entrada_precio = null;
  let entrada_enlace = null;
  if (isEvento) {
    entrada_tipo = form.entradaTipo;
    if (['precio_fijo', 'consumicion', 'otros'].includes(form.entradaTipo) && String(form.entradaDetalle || '').trim()) {
      entrada_precio = String(form.entradaDetalle).trim();
    }
    entrada_enlace = String(form.entradaEnlace || '').trim() || null;
  }

  const cuerpo = {
    pais: form.pais,
    comunidad: null,
    provincia: form.pais === 'ES' ? form.provincia : null,
    provinciaTexto: form.pais !== 'ES' ? String(form.provinciaTexto || '').trim() : null,
    ciudad: String(form.ciudad || '').trim(),
    via: String(form.via || '').trim(),
    cp: String(form.cp || '').trim(),
    fecha: String(form.fecha || '').trim(),
    mapsUrl: String(form.mapsUrl || '').trim() || null,
    notas: String(form.notas || '').trim() || null,
    imagenUrl: normalizeComunidadImagenUrl(form.imagenPreview),
    place,
    musicGenres: normalizeComunidadMusicGenresForSave(form),
  };

  return {
    title: String(form.titulo || '').trim(),
    publicacion_tipo: form.tipo,
    cuerpo,
    entrada_tipo,
    entrada_precio,
    entrada_enlace,
  };
}

/**
 * Construye filas para `comunidad_publicaciones` desde el formulario de PageComunidad.
 * @param {string} userId
 * @param {object} form — mismo shape que `defaultForm` + imagenPreview
 */
export function buildPublicationPayload(userId, form) {
  return {
    author_id: userId,
    ...publicationBodyFromForm(form),
    entradas_verificadas: false,
  };
}

function priceDisplayFromRow(row) {
  const isEvento = row.publicacion_tipo === TIPO_EVENTO;
  if (!isEvento) return TIPO_CORTO_HEADER[row.publicacion_tipo] || 'Sitio';
  const t = row.entrada_tipo;
  const p = row.entrada_precio;
  if (t === 'libre') return 'Gratis';
  if (t === 'donacion') return 'Donación';
  if (t === 'precio_fijo') return (p && String(p).trim()) || 'Fijo';
  if (t === 'consumicion') return 'Consumición';
  if (t === 'invitacion') return 'Invitación';
  if (t === 'por_confirmar') return 'Precio por confirmar';
  if (t === 'otros') return 'Evento';
  return 'Evento';
}

export function mapComunidadRowToEvent(row, authorUsername, authorAvatarUrl = null) {
  const c = row.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {};
  return {
    id: row.id,
    authorId: row.author_id,
    authorUsername: authorUsername || null,
    authorAvatarUrl: resolveProfileAvatarUrl(authorAvatarUrl),
    type: 'Comunidad',
    tag: 'Comunidad',
    title: row.title,
    date: c.fecha && String(c.fecha).trim() ? String(c.fecha).trim() : 'Por concretar',
    place: c.place && String(c.place).trim() ? String(c.place).trim() : '',
    price: priceDisplayFromRow(row),
    color: 'rgba(200,168,75,0.1)',
    asistiré: 0,
    interesa: 0,
    extraTipo: row.publicacion_tipo || null,
    notas: c.notas && String(c.notas).trim() ? String(c.notas).trim() : null,
    mapsUrl: c.mapsUrl && String(c.mapsUrl).trim() ? String(c.mapsUrl).trim() : null,
    entrada_tipo: row.entrada_tipo,
    entrada_precio: row.entrada_precio,
    entrada_enlace: row.entrada_enlace,
    entradas_verificadas: !!row.entradas_verificadas,
    imagenUrl: c.imagenUrl && String(c.imagenUrl).trim() ? String(c.imagenUrl).trim() : null,
    aprobada: !!row.aprobada,
    isHidden: !!row.is_hidden,
    destacada: !!row.destacada,
    destacadaSolicitudPendiente: !!row.destacada_solicitud_pendiente,
    destacadaGroovesCobrados: row.destacada_grooves_cobrados != null ? Number(row.destacada_grooves_cobrados) : null,
    createdAtDb: row.created_at || null,
    fuenteFecha: row?.fuente_fecha ? String(row.fuente_fecha).trim() : '',
    musicGenres: musicGenresFromCuerpo(c),
    metadata: row?.metadata && typeof row.metadata === 'object' ? row.metadata : null,
    _cuerpo: c,
    _db: true,
  };
}

export function eventToEditForm(ev) {
  const c = ev._cuerpo && typeof ev._cuerpo === 'object' ? ev._cuerpo : {};
  return {
    titulo: ev.title || '',
    tipo: ev.extraTipo || TIPO_EVENTO,
    pais: c.pais || 'ES',
    provincia: c.provincia || '',
    provinciaTexto: c.provinciaTexto || '',
    ciudad: c.ciudad || '',
    via: c.via || '',
    cp: c.cp || '',
    mapsUrl: c.mapsUrl || '',
    fecha: c.fecha || '',
    entradaTipo: ev.entrada_tipo || 'libre',
    entradaDetalle: ev.entrada_precio || '',
    entradaEnlace: ev.entrada_enlace || '',
    notas: ev.notas || '',
    imagenPreview: ev.imagenUrl || null,
    musicGenreMode: musicGenreModeFromGenres(musicGenresFromCuerpo(c)),
    musicGenres: musicGenresFromCuerpo(c).filter(
      g => g !== COMUNIDAD_GENERO_MULTICULTURAL && g !== COMUNIDAD_GENERO_DESCONOCIDO
    ),
  };
}

/** Formulario de edición (mismo shape que PageComunidad) a partir de una fila de BD. */
export function comunidadDbRowToAdminForm(row) {
  return {
    ...eventToEditForm(mapComunidadRowToEvent(row, null)),
    fuenteUrl: row?.fuente_url ? String(row.fuente_url) : '',
    fuenteFecha: row?.fuente_fecha ? String(row.fuente_fecha) : '',
  };
}

/** Admin: actualiza cualquier publicación por id (RLS `comunidad_update_admin`). */
export async function adminUpdateComunidadPublicacion(publicationId, form) {
  const body = {
    ...publicationBodyFromForm(form),
    fuente_url: String(form.fuenteUrl ?? '').trim() || null,
    fuente_fecha: String(form.fuenteFecha ?? '').trim() || null,
  };
  return supabase.from('comunidad_publicaciones').update(body).eq('id', publicationId);
}

export async function fetchComunidadPublicaciones({ includeHidden = false } = {}) {
  let q = supabase
    .from('comunidad_publicaciones')
    .select(
      'id, author_id, title, publicacion_tipo, cuerpo, created_at, fuente_fecha, entrada_tipo, entrada_precio, entrada_enlace, entradas_verificadas, is_hidden, aprobada, destacada, destacada_solicitud_pendiente, destacada_grooves_cobrados, metadata'
    )
    .order('created_at', { ascending: false })
    .limit(100);
  if (!includeHidden) q = q.eq('is_hidden', false);
  const { data: rows, error } = await q;
  if (error) {
    console.error('[comunidadPublicaciones] fetch:', error);
    return [];
  }
  const list = rows || [];
  const authorIds = [...new Set(list.map(r => r.author_id).filter(Boolean))];
  let authorsById = {};
  if (authorIds.length) {
    const { data: profs } = await supabase.from('profiles').select('id, username, avatar_url').in('id', authorIds);
    authorsById = Object.fromEntries(
      (profs || []).map(p => [
        p.id,
        { username: p.username, avatar_url: resolveProfileAvatarUrl(p.avatar_url) },
      ])
    );
  }
  return list.map(r => {
    const a = authorsById[r.author_id];
    return mapComunidadRowToEvent(r, a?.username, a?.avatar_url);
  });
}

/** Partner: solicita aparecer en portada (pendiente de que admin marque destacada y cobre grooves). */
export async function solicitarDestacadoComunidad(publicationId, authorUserId) {
  return supabase
    .from('comunidad_publicaciones')
    .update({ destacada_solicitud_pendiente: true })
    .eq('id', publicationId)
    .eq('author_id', authorUserId)
    .eq('aprobada', true)
    .eq('destacada', false);
}

export async function cancelarSolicitudDestacadoComunidad(publicationId, authorUserId) {
  return supabase
    .from('comunidad_publicaciones')
    .update({ destacada_solicitud_pendiente: false })
    .eq('id', publicationId)
    .eq('author_id', authorUserId);
}

export async function insertComunidadPublicacion(userId, form) {
  const payload = buildPublicationPayload(userId, form);
  return supabase.from('comunidad_publicaciones').insert(payload).select('id').single();
}

export async function updateComunidadPublicacion(userId, publicationId, form) {
  return supabase
    .from('comunidad_publicaciones')
    .update(publicationBodyFromForm(form))
    .eq('id', publicationId)
    .eq('author_id', userId);
}

export async function deleteComunidadPublicacion(userId, publicationId) {
  return supabase.from('comunidad_publicaciones').delete().eq('id', publicationId).eq('author_id', userId);
}

export async function adminDeleteComunidadPublicacion(publicationId) {
  return supabase.from('comunidad_publicaciones').delete().eq('id', publicationId);
}

export async function setComunidadPublicacionHidden(publicationId, { hidden, reason = null } = {}) {
  const patch = hidden
    ? { is_hidden: true, hidden_reason: reason || null, hidden_at: new Date().toISOString() }
    : { is_hidden: false, hidden_reason: null, hidden_at: null };
  return supabase.from('comunidad_publicaciones').update(patch).eq('id', publicationId);
}

export { TIPO_EVENTO };
